import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, apiError } from "../lib/api";

export const INCIDENT_CATEGORIES = [
  "Problema de registro", "Problema de pago", "QR no disponible", "Acceso no autorizado",
  "Cambio de taller", "Foto o identificación", "Registro duplicado", "Problema técnico",
  "Exceso de cupo", "Problema de salón", "Otro",
];

const PRIORITIES = [
  { id: "low", label: "Baja" },
  { id: "medium", label: "Media" },
  { id: "high", label: "Alta" },
  { id: "critical", label: "Crítica" },
];

export const IncidentModal = ({ open, onClose, workshopId = null, defaultFolio = "" }) => {
  const [form, setForm] = useState({ category: "Otro", priority: "medium", description: "", attendee_folio: defaultFolio });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (form.description.trim().length < 5) {
      toast.error("Describe la incidencia (mínimo 5 caracteres).");
      return;
    }
    setSaving(true);
    try {
      await api.post("/incidents", {
        category: form.category,
        priority: form.priority,
        description: form.description.trim(),
        attendee_folio: form.attendee_folio.trim() || null,
        workshop_id: workshopId,
      });
      toast.success("Incidencia reportada");
      setForm({ category: "Otro", priority: "medium", description: "", attendee_folio: "" });
      onClose(true);
    } catch (err) {
      toast.error(apiError(err, "No se pudo reportar la incidencia."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="font-bold text-hz-navy">Reportar incidencia</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="incident-category" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INCIDENT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Prioridad</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="incident-priority" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Folio relacionado (opcional)</Label>
            <Input value={form.attendee_folio} onChange={(e) => setForm({ ...form, attendee_folio: e.target.value })} data-testid="incident-folio" className="mt-1" placeholder="Ej. HZ26-000001" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Descripción *</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="incident-description" className="mt-1" placeholder="Describe lo sucedido…" />
          </div>
          <Button onClick={submit} disabled={saving} data-testid="incident-submit" className="w-full bg-hz-navy font-bold text-white hover:bg-hz-blue">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Reportar incidencia
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
